import api from "../../Environment";

/**
 * Client helpers for the generic Legal Document & Consent system.
 *
 * The document TEXT is never stored in React — it is always fetched from the
 * backend (DB/Admin is the source of truth) and is sanitized server-side.
 */

/**
 * Fetch the current published document (title, version, dates, sanitized HTML).
 * @param {string} slug e.g. "privacy_policy"
 */
export async function fetchLegalDocument(slug) {
  const response = await api.getMethod(
    "legal_documents/view?slug=" + encodeURIComponent(slug)
  );
  const body = response && response.data;
  if (body && body.success === true && body.data) {
    return body.data;
  }
  return null;
}

/**
 * Fetch the currently-required documents for an audience (no body content).
 * @param {"new"|"existing"} audience
 * @returns {Promise<Array>} list of { document_id, slug, title, version, version_id, url }
 */
export async function fetchRequiredDocuments(audience) {
  const aud = audience === "existing" ? "existing" : "new";
  const response = await api.getMethod(
    "legal_documents/required?audience=" + aud
  );
  const body = response && response.data;
  if (body && body.success === true && body.data && Array.isArray(body.data.documents)) {
    return body.data.documents;
  }
  return [];
}

/**
 * Fetch the authenticated user's OUTSTANDING required documents.
 * @returns {Promise<{count:number, documents:Array}>}
 */
export async function fetchOutstandingDocuments() {
  const response = await api.postMethod("legal_documents/outstanding", {});
  const body = response && response.data;
  if (body && body.success === true && body.data) {
    return {
      count: Number(body.data.outstanding_required_documents || 0),
      documents: Array.isArray(body.data.documents) ? body.data.documents : [],
    };
  }
  return { count: 0, documents: [] };
}

/**
 * Record acceptance of a specific published version for the current user.
 * @param {number} versionId
 */
export async function acceptLegalDocumentVersion(versionId) {
  const response = await api.postMethod("legal_documents/accept", {
    legal_document_version_id: versionId,
  });
  return response && response.data;
}

/** Public route for a document slug. */
export function legalDocumentPath(slug) {
  return "/legal/" + encodeURIComponent(slug);
}
